/**
 * PRP-WEB-1 W3.1: zoom and pan as a view transform only. The plan is always drawn in plan
 * feet; the view is the SVG view box (plan feet) and the browser maps it onto the element
 * with the default `xMidYMid meet`. Nothing here touches the plan.
 */
import { planBounds, type Plan, type Pt } from '@sketch-core';

/** The visible window, in plan feet. */
export interface ViewBox { x: number; y: number; w: number; h: number }

/** The plan's size and label font size (plan feet), as the drawing has always used them. */
export function planMetrics(plan: Plan): { span: number; pad: number; fs: number } {
  const b = planBounds(plan);
  const span = b ? Math.max(b[2] - b[0], b[3] - b[1], 1) : 1;
  return { span, pad: Math.max(2.5, span * 0.08), fs: Math.max(0.55, span / 42) };
}

/** The whole plan with padding: the read-only thumbnail's framing and the page's Fit. */
export function fitViewBox(plan: Plan): ViewBox {
  const b = planBounds(plan) ?? [0, 0, 1, 1];
  const { pad } = planMetrics(plan);
  return { x: b[0] - pad, y: b[1] - pad, w: b[2] - b[0] + 2 * pad, h: b[3] - b[1] + 2 * pad };
}

export const viewBoxAttr = (v: ViewBox): string => `${v.x} ${v.y} ${v.w} ${v.h}`;

/** Zoom limits relative to Fit: out to twice the plan, in to 1/40 of it (a door jamb fills the screen). */
export const ZOOM_OUT_MAX = 2;
export const ZOOM_IN_MAX = 40;
/** One + / - press or wheel notch. */
export const ZOOM_STEP = 1.25;

/**
 * Zoom by `factor` (> 1 = closer) keeping the plan point `at` where it is on screen.
 * The result is clamped to the limits around `fit`.
 */
export function zoomAt(v: ViewBox, factor: number, at: Pt, fit: ViewBox): ViewBox {
  const minW = fit.w / ZOOM_IN_MAX, maxW = fit.w * ZOOM_OUT_MAX;
  const w = Math.min(maxW, Math.max(minW, v.w / factor));
  const k = w / v.w;
  return { x: at.x - (at.x - v.x) * k, y: at.y - (at.y - v.y) * k, w, h: v.h * k };
}

/** The view's centre. */
export const centerOf = (v: ViewBox): Pt => ({ x: v.x + v.w / 2, y: v.y + v.h / 2 });

/** Move the window by a plan-feet delta (drag right = negative dx: the plan follows the finger). */
export const panBy = (v: ViewBox, dx: number, dy: number): ViewBox => ({ ...v, x: v.x + dx, y: v.y + dy });

/** Plan feet per screen pixel for an element of this size (`meet`: the tighter axis wins). */
export function feetPerPx(v: ViewBox, elW: number, elH: number): number {
  if (elW <= 0 || elH <= 0) return v.w / 800;
  return Math.max(v.w / elW, v.h / elH);
}

/** A client point inside the element's rect, as a plan point (inverse of `meet`). */
export function clientToPlan(v: ViewBox, rect: { left: number; top: number; width: number; height: number }, cx: number, cy: number): Pt {
  const k = feetPerPx(v, rect.width, rect.height);
  const offX = (rect.width - v.w / k) / 2, offY = (rect.height - v.h / k) / 2;
  return { x: v.x + (cx - rect.left - offX) * k, y: v.y + (cy - rect.top - offY) * k };
}

/** How many times closer than Fit the view is (1 = Fit), for the zoom read-out. */
export const zoomLevel = (v: ViewBox, fit: ViewBox): number => fit.w / v.w;
